import { useState } from "react";
import { format } from "date-fns";
import { Plus } from "lucide-react";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Switch } from "../../ui/switch";
import { Textarea } from "../../ui/textarea";
import { cn } from "../../utils/common/cn";
import {
  describeScope,
  type RbpeAlertRule,
  type RbpeAlertRuleDraft,
  type RbpeAlertRuleProposal,
  type RbpeAlertRuleScope,
} from "../../pages/AthenaPricingEngine/lib/api/rbpe-alert-rules";
import { useOffices } from "../../pages/AthenaPricingEngine/lib/api/reference";

type AlertType = "deal_floor" | "segment_drift";

const ALERT_TYPE_LABELS: Record<AlertType, string> = {
  deal_floor: "Deal Falls Below Floor",
  segment_drift: "Segment Drifts Below Floor",
};

function scopeLabel(officeScope: RbpeAlertRule["officeScope"]) {
  return officeScope === "all" ? "All offices" : officeScope.join(", ");
}

export function ActiveAlertRulesTable({ rules }: { rules: RbpeAlertRule[] }) {
  return (
    <div className="overflow-x-auto border border-border bg-card">
      <table className="w-full min-w-[820px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border bg-secondary/50">
            {["Alert Type", "Thresholds", "Office Name Scope", "Recipients", "Status"].map((head) => (
              <th
                key={head}
                className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
              >
                {head}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rules.map((rule) => (
            <tr key={rule.id} className="border-b border-border/60 last:border-b-0">
              <td className="px-3 py-2.5 font-medium text-foreground">
                {ALERT_TYPE_LABELS[rule.scope.type]}
              </td>
              <td className="px-3 py-2.5 tabular-nums text-foreground/90">
                {rule.scope.type === "deal_floor"
                  ? `RAV floor ${rule.scope.ravFloorPct.toFixed(2)}%`
                  : `${rule.scope.relativeMovePct.toFixed(1)}% relative · ${rule.scope.absoluteFloorBps} bps absolute`}
              </td>
              <td className="px-3 py-2.5 text-muted-foreground">
                {scopeLabel(rule.officeScope)}
              </td>
              <td className="px-3 py-2.5 text-xs text-muted-foreground">
                {rule.notificationRecipients.join(", ")}
              </td>
              <td className="px-3 py-2.5">
                <span
                  className={cn(
                    "inline-flex items-center border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider",
                    rule.active
                      ? "border-approve/40 bg-approve/10 text-approve"
                      : "border-border bg-secondary text-muted-foreground",
                  )}
                >
                  {rule.active ? "Active" : "Paused"}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ProposeNewButton({ onClick }: { onClick: () => void }) {
  return (
    <Button size="sm" className="h-9 text-xs" onClick={onClick}>
      <Plus className="mr-1 size-3.5" />
      Propose New Rule
    </Button>
  );
}

export function AlertRuleDialog({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (draft: RbpeAlertRuleDraft) => Promise<unknown> | void;
  isSubmitting: boolean;
}) {
  const officesQuery = useOffices();
  const offices = (officesQuery.data ?? []).map((o) => o.officeName);
  const [alertType, setAlertType] = useState<AlertType>("deal_floor");
  const [ravFloorPct, setRavFloorPct] = useState("0.25");
  const [relativeMovePct, setRelativeMovePct] = useState("10");
  const [absoluteFloorBps, setAbsoluteFloorBps] = useState("60");
  const [officeMode, setOfficeMode] = useState<"all" | "specific">("all");
  const [selectedOffices, setSelectedOffices] = useState<string[]>([]);
  const [recipients, setRecipients] = useState<string[]>([]);
  const [recipientInput, setRecipientInput] = useState("");
  const [active, setActive] = useState(true);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setAlertType("deal_floor");
    setRavFloorPct("0.25");
    setRelativeMovePct("10");
    setAbsoluteFloorBps("60");
    setOfficeMode("all");
    setSelectedOffices([]);
    setRecipients([]);
    setRecipientInput("");
    setActive(true);
    setReason("");
    setError(null);
  }

  function toggleOffice(office: string) {
    setSelectedOffices((previous) =>
      previous.includes(office)
        ? previous.filter((item) => item !== office)
        : [...previous, office],
    );
  }

  // Mirrors ProductRoleAssigner.jsx's picker + "Add" pattern (used on /products'
  // role-adding UI), adapted for free-text email input instead of two dropdowns.
  function addRecipient() {
    const email = recipientInput.trim();
    if (!email) return;
    setRecipients((previous) => (previous.includes(email) ? previous : [...previous, email]));
    setRecipientInput("");
  }

  function removeRecipient(email: string) {
    setRecipients((previous) => previous.filter((item) => item !== email));
  }

  async function handleSubmit() {
    setError(null);

    if (reason.trim() === "") {
      setError("A reason is required — every rule change is reviewed by someone else.");
      return;
    }

    let scope: RbpeAlertRuleScope;
    if (alertType === "deal_floor") {
      const floor = Number(ravFloorPct);
      if (!Number.isFinite(floor) || floor <= 0) {
        setError("Enter a positive threshold, or scope the rule to All Offices.");
        return;
      }
      scope = { type: "deal_floor", ravFloorPct: floor };
    } else {
      const relative = Number(relativeMovePct);
      const absolute = Number(absoluteFloorBps);
      if (
        !Number.isFinite(relative) ||
        relative <= 0 ||
        !Number.isFinite(absolute) ||
        absolute <= 0
      ) {
        setError("Enter a positive threshold, or scope the rule to All Offices.");
        return;
      }
      scope = {
        type: "segment_drift",
        relativeMovePct: relative,
        absoluteFloorBps: Math.round(absolute),
      };
    }

    if (officeMode === "specific" && selectedOffices.length === 0) {
      setError("Enter a positive threshold, or scope the rule to All Offices.");
      return;
    }

    if (recipients.length === 0) {
      setError("Add at least one recipient.");
      return;
    }

    await onSubmit({
      scope,
      officeScope: officeMode === "all" ? "all" : selectedOffices,
      active,
      notificationRecipients: recipients,
      reason: reason.trim(),
    });

    reset();
    onOpenChange(false);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Propose An Alert Rule</DialogTitle>
          <DialogDescription>
            Proposals go to the approval queue. Nothing changes production until someone approves.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="space-y-2">
            <Label>Alert Type</Label>
            <div className="grid gap-2 sm:grid-cols-2">
              {(Object.keys(ALERT_TYPE_LABELS) as AlertType[]).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setAlertType(type)}
                  className={cn(
                    "border px-3 py-2 text-left text-xs",
                    alertType === type
                      ? "border-primary/60 bg-amber-dim text-foreground"
                      : "border-border bg-secondary/40 text-muted-foreground hover:text-foreground",
                  )}
                >
                  {ALERT_TYPE_LABELS[type]}
                </button>
              ))}
            </div>
          </div>

          {alertType === "deal_floor" ? (
            <div className="space-y-2">
              <Label htmlFor="rav-floor">RAV Floor %</Label>
              <Input
                id="rav-floor"
                inputMode="decimal"
                value={ravFloorPct}
                onChange={(event) => setRavFloorPct(event.target.value)}
              />
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {/* Same bottom-aligned pattern as the Manage Targets dialog. These two
                  labels are short enough not to wrap today, so this changes nothing
                  visible -- it stops the inputs drifting out of line the moment one
                  does, at a narrow width or a longer label. */}
              <div className="flex h-full flex-col">
                <Label htmlFor="relative-move">Relative Move %</Label>
                <div className="mt-auto pt-2">
                  <Input
                    id="relative-move"
                    inputMode="decimal"
                    value={relativeMovePct}
                    onChange={(event) => setRelativeMovePct(event.target.value)}
                  />
                </div>
              </div>
              <div className="flex h-full flex-col">
                <Label htmlFor="absolute-floor">Absolute Floor (bps)</Label>
                <div className="mt-auto pt-2">
                  <Input
                    id="absolute-floor"
                    inputMode="numeric"
                    value={absoluteFloorBps}
                    onChange={(event) => setAbsoluteFloorBps(event.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>Office Name Scope</Label>
            <div className="flex gap-2">
              {(["all", "specific"] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setOfficeMode(mode)}
                  className={cn(
                    "border px-3 py-1.5 text-xs",
                    officeMode === mode
                      ? "border-primary/60 bg-amber-dim text-foreground"
                      : "border-border bg-secondary/40 text-muted-foreground hover:text-foreground",
                  )}
                >
                  {mode === "all" ? "All offices" : "Specific offices"}
                </button>
              ))}
            </div>
            {officeMode === "specific" ? (
              <div className="mt-2 grid gap-1.5 border border-border bg-secondary/30 p-3 sm:grid-cols-2">
                {offices.map((office) => (
                  <label
                    key={office}
                    className="flex cursor-pointer items-center gap-2 text-xs text-foreground/90"
                  >
                    <input
                      type="checkbox"
                      checked={selectedOffices.includes(office)}
                      onChange={() => toggleOffice(office)}
                      className="size-3.5 accent-[var(--primary)]"
                    />
                    {office}
                  </label>
                ))}
              </div>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="recipients">Recipients</Label>
            <div className="flex gap-2">
              <Input
                id="recipients"
                value={recipientInput}
                maxLength={200}
                placeholder="name@company.com"
                onChange={(event) => setRecipientInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    addRecipient();
                  }
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9 shrink-0 px-3"
                onClick={addRecipient}
              >
                <Plus className="size-3.5" />
              </Button>
            </div>
            {recipients.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {recipients.map((email) => (
                  <span
                    key={email}
                    className="inline-flex items-center gap-1.5 border border-border bg-secondary/40 px-2 py-1 text-xs text-foreground/90"
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() => removeRecipient(email)}
                      aria-label={`Remove ${email}`}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
            ) : null}
          </div>

          <div className="flex items-center justify-between border border-border bg-secondary/30 px-3 py-2.5">
            <Label htmlFor="rule-active" className="text-xs">
              Active on Approval
            </Label>
            <Switch id="rule-active" checked={active} onCheckedChange={setActive} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="rule-reason">Reason</Label>
            <Textarea
              id="rule-reason"
              value={reason}
              maxLength={1000}
              rows={3}
              placeholder="Why this threshold, and what it should catch."
              onChange={(event) => setReason(event.target.value)}
            />
          </div>

          {error ? (
            <p role="alert" className="border border-decline/50 bg-decline/10 px-3 py-2 text-xs text-decline">
              {error}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" className="h-9 text-xs" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button size="sm" className="h-9 text-xs" disabled={isSubmitting} onClick={handleSubmit}>
            {isSubmitting ? "Submitting…" : "Submit For Approval"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ApprovalQueue({
  proposals,
  onDecide,
  isDeciding,
}: {
  proposals: RbpeAlertRuleProposal[];
  onDecide: (id: string, status: "approved" | "rejected") => Promise<unknown> | void;
  isDeciding: boolean;
}) {
  if (proposals.length === 0) {
    return <p className="text-sm text-muted-foreground">Nothing waiting for review.</p>;
  }

  return (
    <ul className="divide-y divide-border/60 border border-border bg-card">
      {proposals.map((proposal) => (
        <li key={proposal.id} className="flex flex-wrap items-start justify-between gap-4 p-4">
          <div className="min-w-[16rem] flex-1">
            <p className="text-sm font-medium text-foreground">{describeScope(proposal.scope)}</p>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{proposal.reason}</p>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Proposed {format(new Date(proposal.proposedAt), "d MMM yyyy, HH:mm")}
            </p>
          </div>
          {proposal.status === "pending" ? (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs"
                disabled={isDeciding}
                onClick={() => onDecide(proposal.id, "rejected")}
              >
                Reject
              </Button>
              <Button
                size="sm"
                className="h-8 text-xs"
                disabled={isDeciding}
                onClick={() => onDecide(proposal.id, "approved")}
              >
                Approve
              </Button>
            </div>
          ) : (
            <span
              className={cn(
                "inline-flex items-center border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider",
                proposal.status === "approved"
                  ? "border-approve/40 bg-approve/10 text-approve"
                  : "border-border bg-secondary text-muted-foreground",
              )}
            >
              {proposal.status}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}
